import type { SEOCheck, SEOAnalysis } from '@/types/articles';

// Strip HTML tags from content
const stripHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html || '', 'text/html');
  return doc.body.textContent || '';
};

// Count words in text
const countWords = (text: string): number => {
  return text.trim().split(/\s+/).filter(word => word.length > 0).length;
};

// Count sentences in text
const countSentences = (text: string): number => {
  return text.split(/[.!?]+/).filter(s => s.trim().length > 0).length;
};

// Calculate Flesch Reading Ease Score
const calculateFleschScore = (text: string): number => {
  const words = countWords(text);
  const sentences = countSentences(text);
  const syllables = countSyllables(text);
  
  if (words === 0 || sentences === 0) return 0;
  
  const score = 206.835 - 1.015 * (words / sentences) - 84.6 * (syllables / words);
  return Math.max(0, Math.min(100, Math.round(score)));
};

// Simple syllable counter
const countSyllables = (text: string): number => {
  const words = text.toLowerCase().split(/\s+/);
  let total = 0;
  
  words.forEach(word => {
    word = word.replace(/[^a-z]/g, '');
    if (word.length <= 3) {
      total += 1;
    } else {
      word = word.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, '');
      word = word.replace(/^y/, '');
      const matches = word.match(/[aeiouy]{1,2}/g);
      total += matches ? matches.length : 1;
    }
  });
  
  return total;
};

// Check if keyphrase appears in text
const keyphraseInText = (text: string, keyphrase: string): boolean => {
  if (!keyphrase) return false;
  return text.toLowerCase().includes(keyphrase.toLowerCase());
};

// Calculate keyphrase density
const calculateKeyphraseDensity = (text: string, keyphrase: string): number => {
  if (!keyphrase || !text) return 0;
  const words = countWords(text);
  if (words === 0) return 0;
  
  const regex = new RegExp(keyphrase.toLowerCase(), 'gi');
  const matches = text.toLowerCase().match(regex) || [];
  const keyphraseWords = countWords(keyphrase);
  
  return (matches.length * keyphraseWords / words) * 100;
};

// Get first paragraph from HTML content
const getFirstParagraph = (html: string): string => {
  const doc = new DOMParser().parseFromString(html || '', 'text/html');
  const firstP = doc.querySelector('p');
  return firstP?.textContent || '';
};

// Check for passive voice (simple detection)
const countPassiveVoice = (text: string): number => {
  const passivePatterns = /\b(am|is|are|was|were|be|been|being)\s+\w+ed\b/gi;
  const matches = text.match(passivePatterns) || [];
  return matches.length;
};

// Check for transition words
const countTransitionWords = (text: string): number => {
  const transitions = [
    'however', 'therefore', 'furthermore', 'moreover', 'consequently',
    'additionally', 'meanwhile', 'nevertheless', 'similarly', 'likewise',
    'in addition', 'for example', 'for instance', 'in conclusion', 'as a result',
    'on the other hand', 'in contrast', 'first', 'second', 'third', 'finally',
    'next', 'then', 'also', 'besides', 'thus', 'hence', 'accordingly'
  ];
  
  let count = 0;
  transitions.forEach(word => {
    const regex = new RegExp(`\\b${word}\\b`, 'gi');
    const matches = text.match(regex) || [];
    count += matches.length;
  });
  
  return count;
};

export const analyzeArticleSEO = (
  title: string,
  slug: string,
  content: string,
  metaTitle: string,
  metaDescription: string,
  focusKeyword: string
): SEOAnalysis => {
  const checks: SEOCheck[] = [];
  const plainContent = stripHtml(content);
  const wordCount = countWords(plainContent);
  const sentenceCount = countSentences(plainContent);
  const avgSentenceLength = sentenceCount > 0 ? wordCount / sentenceCount : 0;
  
  // Keyphrase checks
  if (!focusKeyword) {
    checks.push({
      id: 'keyphrase-set',
      label: 'Focus keyphrase',
      status: 'error',
      message: 'No focus keyphrase set. Add a keyphrase to optimize your content.',
      category: 'keyphrase'
    });
  } else {
    checks.push({
      id: 'keyphrase-set',
      label: 'Focus keyphrase',
      status: 'good',
      message: 'Focus keyphrase is set.',
      category: 'keyphrase'
    });
    
    // Keyphrase in title
    const titleHasKeyphrase = keyphraseInText(title, focusKeyword);
    checks.push({
      id: 'keyphrase-title',
      label: 'Keyphrase in title',
      status: titleHasKeyphrase ? 'good' : 'error',
      message: titleHasKeyphrase 
        ? 'Keyphrase appears in the title.' 
        : 'Keyphrase not found in title. Include it for better SEO.',
      category: 'keyphrase'
    });
    
    // Keyphrase in meta title
    const metaTitleHasKeyphrase = keyphraseInText(metaTitle || title, focusKeyword);
    checks.push({
      id: 'keyphrase-meta-title',
      label: 'Keyphrase in meta title',
      status: metaTitleHasKeyphrase ? 'good' : 'warning',
      message: metaTitleHasKeyphrase 
        ? 'Keyphrase appears in meta title.' 
        : 'Consider adding keyphrase to meta title.',
      category: 'keyphrase'
    });
    
    // Keyphrase in slug
    const slugHasKeyphrase = slug.toLowerCase().includes(focusKeyword.toLowerCase().replace(/\s+/g, '-'));
    checks.push({
      id: 'keyphrase-slug',
      label: 'Keyphrase in URL',
      status: slugHasKeyphrase ? 'good' : 'warning',
      message: slugHasKeyphrase 
        ? 'Keyphrase appears in the URL.' 
        : 'Consider including keyphrase in the URL slug.',
      category: 'keyphrase'
    });
    
    // Keyphrase in meta description
    const metaDescHasKeyphrase = keyphraseInText(metaDescription || '', focusKeyword);
    checks.push({
      id: 'keyphrase-meta-desc',
      label: 'Keyphrase in meta description',
      status: metaDescHasKeyphrase ? 'good' : 'warning',
      message: metaDescHasKeyphrase 
        ? 'Keyphrase appears in meta description.' 
        : 'Consider adding keyphrase to meta description.',
      category: 'keyphrase'
    });
    
    // Keyphrase in first paragraph
    const firstParagraph = getFirstParagraph(content);
    const introHasKeyphrase = keyphraseInText(firstParagraph, focusKeyword);
    checks.push({
      id: 'keyphrase-intro',
      label: 'Keyphrase in introduction',
      status: introHasKeyphrase ? 'good' : 'warning',
      message: introHasKeyphrase 
        ? 'Keyphrase appears in the introduction.' 
        : 'Include keyphrase in the first paragraph for better SEO.',
      category: 'keyphrase'
    });
    
    // Keyphrase density
    const density = calculateKeyphraseDensity(plainContent, focusKeyword);
    let densityStatus: 'good' | 'warning' | 'error' = 'good';
    let densityMessage = `Keyphrase density is ${density.toFixed(1)}% (target: 1-3%).`;
    
    if (density < 0.5) {
      densityStatus = 'error';
      densityMessage = `Keyphrase density is too low (${density.toFixed(1)}%). Target: 1-3%.`;
    } else if (density < 1) {
      densityStatus = 'warning';
      densityMessage = `Keyphrase density is slightly low (${density.toFixed(1)}%). Target: 1-3%.`;
    } else if (density > 3) {
      densityStatus = 'warning';
      densityMessage = `Keyphrase density is high (${density.toFixed(1)}%). Avoid keyword stuffing.`;
    }
    
    checks.push({
      id: 'keyphrase-density',
      label: 'Keyphrase density',
      status: densityStatus,
      message: densityMessage,
      category: 'keyphrase'
    });
  }
  
  // Content checks
  // Meta title length
  const effectiveTitle = metaTitle || title;
  const titleLength = effectiveTitle.length;
  let titleStatus: 'good' | 'warning' | 'error' = 'good';
  let titleMessage = `Meta title is ${titleLength} characters (target: 30-60).`;
  
  if (titleLength < 30) {
    titleStatus = 'warning';
    titleMessage = `Meta title is too short (${titleLength} chars). Target: 30-60 characters.`;
  } else if (titleLength > 60) {
    titleStatus = 'warning';
    titleMessage = `Meta title may be truncated (${titleLength} chars). Keep under 60 characters.`;
  }
  
  checks.push({
    id: 'title-length',
    label: 'Meta title length',
    status: titleStatus,
    message: titleMessage,
    category: 'content'
  });
  
  // Meta description length
  const metaDescLength = (metaDescription || '').length;
  let metaDescStatus: 'good' | 'warning' | 'error' = metaDescLength === 0 ? 'error' : 'good';
  let metaDescMessage = metaDescLength === 0 
    ? 'No meta description set. Add one for better search results.'
    : `Meta description is ${metaDescLength} characters (target: 120-160).`;
  
  if (metaDescLength > 0) {
    if (metaDescLength < 120) {
      metaDescStatus = 'warning';
      metaDescMessage = `Meta description is short (${metaDescLength} chars). Target: 120-160 characters.`;
    } else if (metaDescLength > 160) {
      metaDescStatus = 'warning';
      metaDescMessage = `Meta description may be truncated (${metaDescLength} chars). Keep under 160 characters.`;
    }
  }
  
  checks.push({
    id: 'meta-desc-length',
    label: 'Meta description length',
    status: metaDescStatus,
    message: metaDescMessage,
    category: 'content'
  });
  
  // Content length
  let contentStatus: 'good' | 'warning' | 'error' = 'good';
  let contentMessage = `Content has ${wordCount} words.`;
  
  if (wordCount < 300) {
    contentStatus = 'error';
    contentMessage = `Content is too short (${wordCount} words). Aim for at least 300 words.`;
  } else if (wordCount < 600) {
    contentStatus = 'warning';
    contentMessage = `Content is relatively short (${wordCount} words). Consider expanding to 600+ words.`;
  } else if (wordCount >= 1500) {
    contentMessage = `Excellent! Content has ${wordCount} words - comprehensive coverage.`;
  }
  
  checks.push({
    id: 'content-length',
    label: 'Content length',
    status: contentStatus,
    message: contentMessage,
    category: 'content'
  });
  
  // Readability checks
  // Sentence length
  let sentenceStatus: 'good' | 'warning' | 'error' = 'good';
  let sentenceMessage = `Average sentence length is ${avgSentenceLength.toFixed(1)} words (target: <20).`;
  
  if (avgSentenceLength > 25) {
    sentenceStatus = 'error';
    sentenceMessage = `Sentences are too long (avg ${avgSentenceLength.toFixed(1)} words). Keep under 20 words.`;
  } else if (avgSentenceLength > 20) {
    sentenceStatus = 'warning';
    sentenceMessage = `Sentences are slightly long (avg ${avgSentenceLength.toFixed(1)} words). Target: under 20 words.`;
  }
  
  checks.push({
    id: 'sentence-length',
    label: 'Sentence length',
    status: sentenceStatus,
    message: sentenceMessage,
    category: 'readability'
  });
  
  // Passive voice
  const passiveCount = countPassiveVoice(plainContent);
  const passivePercentage = sentenceCount > 0 ? (passiveCount / sentenceCount) * 100 : 0;
  let passiveStatus: 'good' | 'warning' | 'error' = 'good';
  let passiveMessage = `Passive voice usage is ${passivePercentage.toFixed(0)}% (target: <10%).`;
  
  if (passivePercentage > 15) {
    passiveStatus = 'error';
    passiveMessage = `Too much passive voice (${passivePercentage.toFixed(0)}%). Use active voice more.`;
  } else if (passivePercentage > 10) {
    passiveStatus = 'warning';
    passiveMessage = `Passive voice is slightly high (${passivePercentage.toFixed(0)}%). Try using more active voice.`;
  }
  
  checks.push({
    id: 'passive-voice',
    label: 'Passive voice',
    status: passiveStatus,
    message: passiveMessage,
    category: 'readability'
  });
  
  // Transition words
  const transitionCount = countTransitionWords(plainContent);
  const transitionPercentage = sentenceCount > 0 ? (transitionCount / sentenceCount) * 100 : 0;
  let transitionStatus: 'good' | 'warning' | 'error' = 'good';
  let transitionMessage = `${transitionPercentage.toFixed(0)}% of sentences contain transition words.`;
  
  if (transitionPercentage < 20) {
    transitionStatus = 'warning';
    transitionMessage = `Only ${transitionPercentage.toFixed(0)}% of sentences have transition words. Add more for better flow.`;
  } else if (transitionPercentage >= 30) {
    transitionMessage = `Great! ${transitionPercentage.toFixed(0)}% of sentences have transition words.`;
  }
  
  checks.push({
    id: 'transition-words',
    label: 'Transition words',
    status: transitionStatus,
    message: transitionMessage,
    category: 'readability'
  });
  
  // Flesch reading ease
  const fleschScore = calculateFleschScore(plainContent);
  let fleschStatus: 'good' | 'warning' | 'error' = 'good';
  let fleschMessage = `Flesch Reading Ease: ${fleschScore} - `;
  
  if (fleschScore >= 60) {
    fleschMessage += 'Easy to read.';
  } else if (fleschScore >= 50) {
    fleschStatus = 'warning';
    fleschMessage += 'Fairly difficult. Consider simplifying.';
  } else {
    fleschStatus = 'error';
    fleschMessage += 'Difficult to read. Simplify your text.';
  }
  
  checks.push({
    id: 'flesch-score',
    label: 'Flesch Reading Ease',
    status: fleschStatus,
    message: fleschMessage,
    category: 'readability'
  });
  
  // Calculate scores
  const seoChecks = checks.filter(c => c.category === 'keyphrase' || c.category === 'content');
  const readabilityChecks = checks.filter(c => c.category === 'readability');
  
  const calculateScore = (checkList: SEOCheck[]): number => {
    if (checkList.length === 0) return 0;
    
    let score = 0;
    checkList.forEach(check => {
      if (check.status === 'good') score += 100;
      else if (check.status === 'warning') score += 60;
      else score += 20;
    });
    
    return Math.round(score / checkList.length);
  };
  
  return {
    seoScore: calculateScore(seoChecks),
    readabilityScore: calculateScore(readabilityChecks),
    checks
  };
};

export const getScoreColor = (score: number): string => {
  if (score >= 70) return 'text-green-600';
  if (score >= 50) return 'text-yellow-600';
  return 'text-red-600';
};

export const getScoreBgColor = (score: number): string => {
  if (score >= 70) return 'bg-green-500';
  if (score >= 50) return 'bg-yellow-500';
  return 'bg-red-500';
};
